import { Navigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { VoiceProvider } from '@/contexts/VoiceProvider'
import { useSSEHandlers } from '@/hooks/useSSEHandlers'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { GlobalCallModal } from '@/components/dashboard/GlobalCallModal'
import { InCallWidget } from '@/components/dashboard/InCallWidget'
import { InactiveTabOverlay } from '@/components/dashboard/InactiveTabOverlay'
import { ManagerLayout } from '@/components/manager/ManagerLayout'
import { OverviewView } from '@/components/manager/OverviewView'
import { HistoryView } from '@/components/manager/HistoryView'
import { AnalyticsView } from '@/components/manager/AnalyticsView'
import { LiveView } from '@/components/manager/LiveView'
import { AIAgentsView } from '@/components/manager/AIAgentsView'
import { CalendarsView } from '@/components/manager/CalendarsView'
import { PowerDialerView } from '@/components/manager/PowerDialerView'
import { BatchCallsView } from '@/components/manager/BatchCallsView'
import { LookupTablesView } from '@/components/manager/LookupTablesView'
import { ConnectedSoftwareView } from '@/components/manager/connectedSoftware/ConnectedSoftwareView'
import { AuthBootProvider } from '@/providers/AuthBootProvider'
import { ManagerBootProvider } from '@/providers/ManagerBootProvider'
import { SettingsView, SETTINGS_SECTION_IDS } from '@/components/manager/SettingsView'
import { NumbersView } from '@/components/manager/NumbersView'
import { useManagerNavigation } from '@/components/manager/useManagerNavigation'
import { useProfile } from '@/api/profile'
import { useSubscriptionStatus } from '@/api/subscription'
import { useAccess } from '@/hooks/useAccess'

/** What a supervisor opens in the admin space: follow the team, set nothing. */
export const SUPERVISOR_TABS = ['live', 'analytics', 'history']

export function ManagerDashboardPage() {
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const { data: profile, isLoading } = useProfile()
  const { canSeeTeamInsights } = useAccess()

  // SSE actif côté manager pour notifs futures (équipe en ligne, batch terminé...)
  useSSEHandlers()

  // Garde : un AGENT n'a pas accès au tableau de bord manager, sauf un
  // superviseur, pour le direct, les analyses et l'historique.
  if (!isLoading && profile?.role === 'AGENT' && !canSeeTeamInsights) {
    return <Navigate to={`/${locale}/dashboard`} replace />
  }

  // VoiceProvider monte Twilio Device + incoming listener + lifecycle dashboard.
  // Le manager fait partie du routing entrant (cf. backend views_webhooks._get_available_web_agents),
  // donc il DOIT avoir un Device enregistré pour que Twilio puisse router les appels vers lui.
  return (
    <VoiceProvider>
      {/* Boot prefetch : queries communes (AuthBoot) + queries Manager.
          Fire-and-forget en idle, ne bloque pas le LCP. Évite les spinners
          au clic sur les tabs Agents/Numéros/Calendriers/etc. */}
      <AuthBootProvider>
        <ManagerBootProvider>
          <ManagerLayout>
            <ManagerContent />
          </ManagerLayout>
        </ManagerBootProvider>
      </AuthBootProvider>
      {/* Modale d'appel entrant (avant accept) — disparaît au connect. */}
      <GlobalCallModal />
      {/* Widget flottant de contrôle d'appel — visible pendant l'appel actif.
          Le dashboard agent a son propre InCallPanel inline; ici on offre la
          version compacte pour que le manager garde la main sur l'appel
          tout en navigant dans l'espace manager. */}
      <InCallWidget />
      <InactiveTabOverlay />
    </VoiceProvider>
  )
}

/** Map d'un activeTab → clé i18n du libellé (réutilisé pour le <title>). */
const TAB_TITLE_KEYS: Record<string, string> = {
  overview: 'manager.tabs.start',
  team: 'manager.tabs.team',
  schedule: 'manager.tabs.schedule',
  'ai-agents': 'manager.tabs.aiAgents',
  'batch-calls': 'manager.tabs.batchCalls',
  'power-dialer': 'manager.tabs.powerDialer',
  calendars: 'manager.tabs.calendars',
  'connected-software': 'manager.tabs.connectedSoftware',
  numbers: 'manager.tabs.numbers',
  lookup: 'manager.tabs.lookup',
  integrations: 'manager.tabs.developers',
  'integrations-marketplace': 'manager.tabs.integrations',
  billing: 'manager.tabs.billing',
  history: 'manager.tabs.history',
  analytics: 'manager.tabs.analytics',
  live: 'manager.tabs.live',
  profile: 'manager.tabs.profile',
  trust: 'manager.tabs.trust',
  settings: 'manager.tabs.settings',
}

function ManagerContent() {
  const { t } = useTranslation()
  const { activeTab, setActiveTab } = useManagerNavigation()
  const { data: abonnement } = useSubscriptionStatus()
  const enEssai = abonnement?.subscription?.status === 'trialing'
  const { isSupervisor, canBilling } = useAccess()

  // Met à jour le <title> du navigateur quand on change d'onglet.
  const titleKey = TAB_TITLE_KEYS[activeTab]
  useDocumentTitle(titleKey ? t(titleKey) : null)

  if (isSupervisor) {
    if (activeTab === 'live') return <LiveView />
    if (activeTab === 'history') return <HistoryView />
    return <AnalyticsView />
  }
  // Facturation : le propriétaire seulement (l'abonnement est à son nom).
  if (activeTab === 'billing' && !canBilling) return <SettingsView section="team" />

  // « Commencer » seulement pendant l'essai ; sinon l'administration s'ouvre sur Agents IA.
  if (activeTab === 'overview') return enEssai ? <OverviewView onTabChange={setActiveTab} /> : <AIAgentsView />
  // Réglages : une page à part avec sa colonne ; ?view=<section> reste l'adresse.
  if (SETTINGS_SECTION_IDS.includes(activeTab)) return <SettingsView section={activeTab} />
  if (activeTab === 'numbers') return <NumbersView />
  if (activeTab === 'history') return <HistoryView />
  if (activeTab === 'analytics') return <AnalyticsView />
  if (activeTab === 'live') return <LiveView />
  if (activeTab === 'lookup') return <LookupTablesView />
  if (activeTab === 'ai-agents') return <AIAgentsView />
  if (activeTab === 'calendars') return <CalendarsView />
  if (activeTab === 'connected-software') return <ConnectedSoftwareView />
  if (activeTab === 'power-dialer') return <PowerDialerView />
  if (activeTab === 'batch-calls') return <BatchCallsView />

  // Onglet inconnu — retour à l'entrée par défaut.
  return enEssai ? <OverviewView onTabChange={setActiveTab} /> : <AIAgentsView />
}
